import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useSocket } from '../contexts/SocketContext';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import AuthModalWidget from './AuthModalWidget';
import api, { API_ENDPOINTS } from '../config/api';
import { Link } from 'react-router-dom';
import { 
  Search, 
  MessageCircle, 
  Bell, 
  User, 
  Moon, 
  Sun,
  Menu,
  X,
  Briefcase,
  Plus,
  Sparkles,
  Users
} from 'lucide-react';
import workloobLogo from '../assets/worklob-logo-cp-no-bg.png';

const NavbarWidget = () => {
  const { user, isAuthenticated, logout, toggleTheme, theme, switchRole } = useAuth();
  const { toggleSidebar } = useSidebar();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState({ jobs: [], gigs: [], users: [] });
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const userMenuRef = useRef(null);

  // Fetch unread message count using dedicated endpoint
  const { data: unreadCountData, refetch: refetchUnreadCount } = useQuery(
    'unreadChatCount',
    () => api.get(API_ENDPOINTS.CHATS.UNREAD_COUNT),
    {
      select: (data) => data.data?.unreadCount || 0,
      enabled: isAuthenticated,
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  const unreadCount = unreadCountData || 0;

  // Fetch unread notification count
  const { data: unreadNotificationCount, refetch: refetchNotificationCount } = useQuery(
    'unreadNotifications',
    () => api.get(API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT),
    {
      select: (data) => data.data?.unreadCount || 0,
      enabled: isAuthenticated,
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  // Listen for socket events to update unread counts
  useEffect(() => {
    if (socket && user?._id) {
      const userId = user._id.toString();
      
      const handleUnreadCountUpdate = (data) => {
        // Only refetch if the update is for the current user
        if (data.userId === userId) {
          console.log('Navbar: Unread count updated for current user, refetching');
          refetchUnreadCount();
        }
      };

      const handleNewMessage = (message) => {
        console.log('Navbar: New message received, refetching unread count');
        refetchUnreadCount(); // Refetch unread count
      };

      const handleNewNotification = () => {
        console.log('Navbar: New notification received, refetching notification count');
        refetchNotificationCount();
      };

      // Listen for unread count updates (when messages are read or new messages arrive)
      socket.on('unread-count-updated', handleUnreadCountUpdate);
      socket.on('new-message', handleNewMessage);
      socket.on('message-sent', handleNewMessage);
      socket.on('new-notification', handleNewNotification);

      return () => {
        socket.off('unread-count-updated', handleUnreadCountUpdate);
        socket.off('new-message', handleNewMessage);
        socket.off('message-sent', handleNewMessage);
        socket.off('new-notification', handleNewNotification);
      };
    }
  }, [socket, refetchUnreadCount, refetchNotificationCount, user?._id]);

  // Debounced search function
  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setSearchResults({ jobs: [], gigs: [], users: [] });
      setShowSearchResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const debounceTimer = setTimeout(async () => {
      try {
        const response = await api.get(API_ENDPOINTS.SEARCH.UNIFIED, {
          params: { q: searchQuery.trim(), limit: 5 }
        });
        setSearchResults(response.data);
        setShowSearchResults(true);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults({ jobs: [], gigs: [], users: [] });
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle result click
  const handleResultClick = (result) => {
    setShowSearchResults(false);
    setSearchQuery('');
    
    if (result.type === 'job') {
      navigate(`/jobs/${result.id}`);
    } else if (result.type === 'gig') {
      navigate(`/gigs/${result.id}`);
    } else if (result.type === 'user') {
      navigate(`/profile/${result.username}`);
    }
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSearchResults(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleCategoryClick = (category) => {
    navigate(`/jobs?category=${encodeURIComponent(category)}`);
  };

  const handleRoleSwitch = async () => {
    await switchRole(navigate, queryClient);
    setIsUserMenuOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // Navigate to profile with username
  const handleProfileClick = () => {
    if (user?.username) {
      navigate(`/profile/${user.username}`);
    } else {
      navigate('/profile');
    }
    setIsUserMenuOpen(false);
  };

  return (
    <header className="bg-surface border-b border-gray-200 dark:border-gray-700 transition-colors duration-300 sticky top-0 z-20">
      {/* First Row */}
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left Side */}
          <div className="flex items-center space-x-4">
            {/* Mobile Menu Button */}
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2 text-gray-600 dark:text-gray-400 hover:text-accent transition-colors duration-200"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Logo on mobile/tablet (name shown in sidebar on lg+) */}
            <Link to="/" className="flex-shrink-0 lg:hidden flex items-center" aria-label="Workloob home">
              <img
                src={workloobLogo}
                alt="Workloob"
                className="h-9 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Search Bar */}
          <div className="flex-1 max-w-md mx-4 hidden md:block relative" ref={searchRef}>
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search jobs, gigs, talents..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (searchQuery.trim().length >= 2) {
                    setShowSearchResults(true);
                  }
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-colors duration-200"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent"></div>
                </div>
              )}
            </form>

            {/* Search Results Dropdown */}
            {showSearchResults && searchQuery.trim().length >= 2 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-96 overflow-y-auto">
                {isSearching ? (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent mx-auto"></div>
                    <p className="mt-2 text-sm">Searching...</p>
                  </div>
                ) : (
                  <>
                    {/* Jobs Results */}
                    {searchResults.jobs && searchResults.jobs.length > 0 && (
                      <div className="border-b border-gray-200 dark:border-gray-700">
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                          <div className="flex items-center space-x-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                            <Briefcase className="w-3 h-3" />
                            <span>Jobs ({searchResults.jobs.length})</span>
                          </div>
                        </div>
                        {searchResults.jobs.map((job) => (
                          <div
                            key={job.id}
                            onClick={() => handleResultClick(job)}
                            className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0 mt-1">
                                <Briefcase className="w-4 h-4 text-blue-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {job.title}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                  {job.description}
                                </p>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {job.category}
                                  </span>
                                  {job.budget?.fixed && (
                                    <span className="text-xs font-medium text-green-600 dark:text-green-400">
                                      ${job.budget.fixed}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Gigs Results */}
                    {searchResults.gigs && searchResults.gigs.length > 0 && (
                      <div className="border-b border-gray-200 dark:border-gray-700">
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                          <div className="flex items-center space-x-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                            <Sparkles className="w-3 h-3" />
                            <span>Gigs ({searchResults.gigs.length})</span>
                          </div>
                        </div>
                        {searchResults.gigs.map((gig) => (
                          <div
                            key={gig.id}
                            onClick={() => handleResultClick(gig)}
                            className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0 mt-1">
                                <Sparkles className="w-4 h-4 text-purple-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {gig.title}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                  {gig.description}
                                </p>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {gig.category}
                                  </span>
                                  {gig.pricing?.fixed && (
                                    <span className="text-xs font-medium text-green-600 dark:text-green-400">
                                      ${gig.pricing.fixed}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Users Results */}
                    {searchResults.users && searchResults.users.length > 0 && (
                      <div>
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                          <div className="flex items-center space-x-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                            <Users className="w-3 h-3" />
                            <span>Talents ({searchResults.users.length})</span>
                          </div>
                        </div>
                        {searchResults.users.map((user) => (
                          <div
                            key={user.id}
                            onClick={() => handleResultClick(user)}
                            className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                {user.profile?.avatar ? (
                                  <img
                                    src={user.profile.avatar}
                                    alt={user.username || 'User'}
                                    className="w-10 h-10 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-white font-medium">
                                    {user.username?.[0]?.toUpperCase() || 'U'}
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {user.firstName && user.lastName
                                    ? `${user.firstName} ${user.lastName}`
                                    : user.username}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  @{user.username}
                                </p>
                                {user.rating > 0 && (
                                  <div className="flex items-center space-x-1 mt-1">
                                    <span className="text-xs text-yellow-500">★</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      {user.rating.toFixed(1)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* No Results */}
                    {!isSearching && 
                     (!searchResults.jobs || searchResults.jobs.length === 0) &&
                     (!searchResults.gigs || searchResults.gigs.length === 0) &&
                     (!searchResults.users || searchResults.users.length === 0) && (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                        <p className="text-sm">No results found for "{searchQuery}"</p>
                        <p className="text-xs mt-1">Try different keywords</p>
                      </div>
                    )}

                    {/* View All Results */}
                    {!isSearching && searchResults.total > 0 && (
                      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={handleSearch}
                          className="w-full text-sm text-accent hover:text-accent/80 font-medium text-center"
                        >
                          View all results ({searchResults.total})
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right Side */}
          <div className="flex items-center space-x-4">
            {/* Post Buttons */}
            {isAuthenticated && (
              <div className="hidden md:flex items-center space-x-2">
                {user?.role === 'talent' && (
                  <button 
                    onClick={() => navigate('/create-gig')}
                    className="btn-outline text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Post Gig
                  </button>
                )}
                {user?.role === 'client' && (
                  <button 
                    onClick={() => navigate('/create-job')}
                    className="btn-outline text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Post Job
                  </button>
                )}
              </div>
            )}

            {/* Chat Icon */}
            {isAuthenticated && (
              <>
                <button 
                  onClick={() => navigate('/chats')}
                  className="relative p-2 text-gray-600 dark:text-gray-400 hover:text-accent transition-colors duration-200"
                >
                  <MessageCircle className="w-6 h-6" />
                  {/* Message Notification Badge - positioned at top-right */}
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 bg-red-500 text-white text-xs font-semibold rounded-full h-5 w-5 flex items-center justify-center transform translate-x-1/2 -translate-y-1/2 min-w-[20px]">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                <button 
                  onClick={() => navigate('/notifications')}
                  className="relative p-2 text-gray-600 dark:text-gray-400 hover:text-accent transition-colors duration-200"
                >
                  <Bell className="w-6 h-6" />
                  {/* Notification Badge - positioned at top-right */}
                  {unreadNotificationCount > 0 && (
                    <span className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-semibold rounded-full h-5 w-5 flex items-center justify-center transform translate-x-1/2 -translate-y-1/2 min-w-[20px]">
                      {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                    </span>
                  )}
                </button>
              </>
            )}


            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-accent transition-colors duration-200"
            >
              {theme === 'light' ? <Moon className="w-6 h-6" /> : <Sun className="w-6 h-6" />}
            </button>

            {/* User Menu / Auth Buttons */}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-accent transition-colors duration-200"
                >
                  {user?.profile?.avatar ? (
                    <img
                      src={user.profile.avatar}
                      alt={user?.username || 'User'}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white font-medium">
                      {user?.profile?.firstName?.[0] || user?.username?.[0]?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <span className="hidden md:block font-medium">{user?.username}</span>
                </button>

                {/* Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                    <button 
                      onClick={handleProfileClick}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Profile
                    </button>
                    <button 
                      onClick={() => {navigate('/settings'); setIsUserMenuOpen(false);}}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Settings
                    </button>
                    <button 
                      onClick={handleRoleSwitch}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Switch to {user?.role === 'talent' ? 'Client' : 'Talent'}
                    </button>
                    <button 
                      onClick={() => {user?.role === 'talent' ? navigate('/create-gig') : navigate('/create-job'); setIsUserMenuOpen(false);}}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {user?.role === 'talent' ? 'Create Gig' : 'Create Job'}
                    </button>
                    <button 
                      onClick={() => {user?.role === 'talent' ? navigate('/jobs') : navigate('/gigs'); setIsUserMenuOpen(false);}}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Browse {user?.role === 'talent' ? 'Jobs' : 'Gigs'}
                    </button>
                    <hr className="my-1 border-gray-200 dark:border-gray-700" />
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => {
                    setAuthModalMode('login');
                    setIsAuthModalOpen(true);
                  }}
                  className="btn-outline text-sm"
                >
                  Login
                </button>
                <button 
                  onClick={() => {
                    setAuthModalMode('signup');
                    setIsAuthModalOpen(true);
                  }}
                  className="btn-primary text-sm"
                >
                  Sign Up
                </button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-600 dark:text-gray-400"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

     

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          <div className="px-4 py-2 space-y-2">
            {/* Search in mobile */}
            <div className="relative">
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                <input
                  type="text"
                  placeholder="Search jobs, gigs, talents..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => {
                    if (searchQuery.trim().length >= 2) {
                      setShowSearchResults(true);
                    }
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent"></div>
                  </div>
                )}
              </form>

              {/* Mobile Search Results */}
              {showSearchResults && searchQuery.trim().length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-96 overflow-y-auto">
                  {isSearching ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent mx-auto"></div>
                      <p className="mt-2 text-sm">Searching...</p>
                    </div>
                  ) : (
                    <>
                      {searchResults.jobs && searchResults.jobs.length > 0 && (
                        <div className="border-b border-gray-200 dark:border-gray-700">
                          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                            <div className="flex items-center space-x-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                              <Briefcase className="w-3 h-3" />
                              <span>Jobs ({searchResults.jobs.length})</span>
                            </div>
                          </div>
                          {searchResults.jobs.map((job) => (
                            <div
                              key={job.id}
                              onClick={() => handleResultClick(job)}
                              className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                            >
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {job.title}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {job.category}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                      {searchResults.gigs && searchResults.gigs.length > 0 && (
                        <div className="border-b border-gray-200 dark:border-gray-700">
                          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                            <div className="flex items-center space-x-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                              <Sparkles className="w-3 h-3" />
                              <span>Gigs ({searchResults.gigs.length})</span>
                            </div>
                          </div>
                          {searchResults.gigs.map((gig) => (
                            <div
                              key={gig.id}
                              onClick={() => handleResultClick(gig)}
                              className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                            >
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {gig.title}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {gig.category}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}

                      {searchResults.users && searchResults.users.length > 0 && (
                        <div>
                          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900">
                            <div className="flex items-center space-x-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                              <Users className="w-3 h-3" />
                              <span>Talents ({searchResults.users.length})</span>
                            </div>
                          </div>
                          {searchResults.users.map((user) => (
                            <div
                              key={user.id}
                              onClick={() => handleResultClick(user)}
                              className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer flex items-center space-x-3"
                            >
                              {user.profile?.avatar ? (
                                <img
                                  src={user.profile.avatar}
                                  alt={user.username || 'User'}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-white text-xs font-medium">
                                  {user.username?.[0]?.toUpperCase() || 'U'}
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {user.firstName && user.lastName
                                    ? `${user.firstName} ${user.lastName}`
                                    : user.username}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  @{user.username}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {!isSearching && 
                       (!searchResults.jobs || searchResults.jobs.length === 0) &&
                       (!searchResults.gigs || searchResults.gigs.length === 0) &&
                       (!searchResults.users || searchResults.users.length === 0) && (
                        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                          <p className="text-sm">No results found</p>
                        </div>
                      )}

                      {!isSearching && searchResults.total > 0 && (
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                          <button
                            onClick={handleSearch}
                            className="w-full text-sm text-accent hover:text-accent/80 font-medium text-center"
                          >
                            View all results ({searchResults.total})
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModalWidget 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)}
        initialMode={authModalMode}
        onWalletConnected={() => {
          // Reopen modal in signup mode after wallet connection
          // Set mode first, then open modal
          setAuthModalMode('signup');
          // Use setTimeout to ensure state updates before reopening
          setTimeout(() => {
            setIsAuthModalOpen(true);
          }, 100);
        }}
      />
    </header>
  );
};

export default NavbarWidget;
